/**
 * Manager statuses — TWO halves of one Р9 surface, unioned (wave 9, T.Y9 + T.Y10;
 * SR v2.1 §3.6/§3.7, BR §3.6, docs/05-dev-spec.md §4, ADDITIVE v1.8.0):
 *
 *   #28   GET   /api/manager-statuses            → `{ data: { statuses: [ManagerStatusDto] } }` (VIEWER)
 *   #28.1 POST  /api/manager-statuses            → `{ data: ManagerStatusDto }` (201)          (ADMIN)
 *   #28.2 PATCH /api/manager-statuses/:statusId  → `{ data: ManagerStatusDto }`                (ADMIN)
 *   #5.5 GET  /api/applications/:pguId/manager-statuses → `{ data: { items: [...] } }`
 *   #5.6 PUT  /api/applications/:pguId/manager-statuses → `{ data: { items: [...] } }`
 *
 * The dictionary is ADMIN-writable through #28.1/#28.2 (the Settings tab). There
 * is deliberately NO DELETE: the journal rows (#5.5/#5.6) reference the dictionary
 * and must keep resolving, so deactivation (`active: false`) is the off-switch —
 * the entry stays, history stays resolvable, already-assigned rows stay assigned,
 * only NEW assignments are refused (#5.6 answers 400 `VALIDATION`).
 *
 * TWO reads of #28 live side by side, on purpose — the parallel wave-9 tracks read
 * the same endpoint differently and both shapes have consumers:
 *
 *   * `fetchManagerStatuses(signal)` — TYPED, active rows only, unwrapped. The
 *     profile block and the register filter (T.Y10) go through
 *     `useManagerStatusDictionary`; the optimistic journal needs the labels.
 *   * `fetchManagerStatusesRaw(includeInactive, signal)` — RAW (`unknown`), the
 *     api's own envelope. The Settings tab (T.Y9) unwraps it in
 *     `pages/settings/statuses-model.ts#parseStatuses`, pure logic the plain-Node
 *     `unit` project can pin, and `?includeInactive=true` is a second READ, not a
 *     client-side filter, because only the api knows the row's `active` flag.
 *     (Union note: this function was `fetchManagerStatuses` on the T.Y9 branch;
 *     the two signatures could not share one name, and nothing outside this
 *     module imported it, so the raw one was renamed and every hook kept its
 *     original name.)
 *
 * The #5.5/#5.6 journal shapes are mirrored from the generated
 * `packages/shared/src/openapi/api-types.ts` 1:1 — the shared barrel cannot
 * re-export that file (the same rule `applications.ts` documents), so the mirror
 * lives in one place. This file is deliberately dedicated: `applications.ts` is a
 * shared conflict surface between the tracks, so everything the statuses need
 * sits here and only two additive lines touch `applications.ts`.
 *
 * T.Z17 (ADDITIVE v1.8.2) wired the register into the same story: `ApplicationDto`
 * carries the resolved `managerStatus` label on every #3 row, and the #5.6 write
 * invalidates the register root too, so the «Статус» cell of the open register
 * follows the drawer live.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiRequest, buildQuery, requestEnvelope } from './client';
// The pure half (the D8 display rule, the replace-set arithmetic, the optimistic
// journal, the T.Z17 invalidation bridge) lives beside the pages so the plain-Node
// unit project can pin it without importing this module — `client.ts` reads
// `import.meta.env`, which does not exist there.
import {
  managerStatusWriteInvalidationKeys,
  optimisticJournal,
} from '../pages/applications/manager-statuses-model';

// ---------------------------------------------------------------------------
// DTOs (mirrors of the generated `components.schemas`)
// ---------------------------------------------------------------------------

/** `ManagerStatusDto` — one #28 dictionary row (1:1 with `apps/api/src/manager-statuses/dto.ts`). */
export interface ManagerStatusDto {
  id: number;
  /** Unique across the dictionary (409 otherwise) — the 409 the settings tab prints. */
  label: string;
  /** `false` = no NEW assignments; history and already-assigned rows are untouched. */
  active: boolean;
  /** The display order of the dictionary and of the pickers that read it. */
  sortOrder: number;
}

/** `ApplicationManagerStatusDto` — one #5.5 journal row. Never deleted. */
export interface ApplicationManagerStatusDto {
  statusId: number;
  /** Resolved from the dictionary so a reader never needs #28 to render a row. */
  label: string;
  assignedAt: string;
  assignedByUserId: number;
  /** `null` on the ACTIVE row — the «removed_at IS NULL» of the D8 model. */
  removedAt: string | null;
  removedByUserId: number | null;
}

/** `{ data: { statuses } }` of #28. */
export interface ManagerStatusListDto {
  statuses: ManagerStatusDto[];
}
/** `{ data: { items } }` of #5.5 / #5.6 — active rows first, then the history. */
export interface ApplicationManagerStatusesDto {
  items: ApplicationManagerStatusDto[];
}

export type ManagerStatusesEnvelope = { data: ManagerStatusListDto };
export type ApplicationManagerStatusesEnvelope = { data: ApplicationManagerStatusesDto };

/** Body of #28.1 — `sortOrder` defaults to «after the last row» on the api. */
export interface ManagerStatusCreateInput {
  label: string;
  sortOrder?: number;
}

/** Body of #28.2 — every key independent, only provided ones change. */
export interface ManagerStatusPatchInput {
  label?: string;
  active?: boolean;
  sortOrder?: number;
}

// ---------------------------------------------------------------------------
// Transport
// ---------------------------------------------------------------------------

const JSON_HEADERS = { 'Content-Type': 'application/json' };

export const MANAGER_STATUSES_PATH = '/manager-statuses';

/** #28 with the defaults dropped — `includeInactive=false` sends nothing, like every list here. */
export function managerStatusesPath(includeInactive = false): string {
  return `${MANAGER_STATUSES_PATH}${buildQuery(includeInactive ? { includeInactive: true } : {})}`;
}

export function managerStatusPath(statusId: number): string {
  return `/manager-statuses/${encodeURIComponent(statusId)}`;
}

/** #5.5 / #5.6 — the journal path of one application. */
export function applicationManagerStatusesPath(pguId: string): string {
  return `/applications/${encodeURIComponent(pguId)}/manager-statuses`;
}

/**
 * #28 — the dictionary, ACTIVE rows only (the contract default), unwrapped. The
 * api answers them `sortOrder`-ordered; the register filter and the profile block
 * need nothing but the active labels.
 */
export async function fetchManagerStatuses(signal?: AbortSignal): Promise<ManagerStatusDto[]> {
  const envelope = await requestEnvelope<ManagerStatusListDto>('GET', MANAGER_STATUSES_PATH, ['data'], { signal });
  return envelope.data?.statuses ?? [];
}

/** #28, RAW — `pages/settings/statuses-model.ts#parseStatuses` unwraps it. */
export async function fetchManagerStatusesRaw(includeInactive: boolean, signal?: AbortSignal): Promise<unknown> {
  return apiRequest<unknown>('GET', managerStatusesPath(includeInactive), { signal });
}

/** #5.5 — the whole journal: active rows (`removedAt IS NULL`) first, then history. */
export async function fetchApplicationManagerStatuses(
  pguId: string,
  signal?: AbortSignal,
): Promise<ApplicationManagerStatusDto[]> {
  const envelope = await requestEnvelope<ApplicationManagerStatusesDto>(
    'GET',
    applicationManagerStatusesPath(pguId),
    ['data'],
    { signal },
  );
  return envelope.data?.items ?? [];
}

/**
 * #5.6 — the REPLACE-SET: every id listed is (re-)assigned, every active id missing
 * from it is closed (both halves land in the journal + the audit row, server-side).
 * `[]` is legal and is exactly how a row becomes «без статуса». An id that is
 * unknown or inactive answers 400 `VALIDATION`.
 */
export async function putApplicationManagerStatuses(
  pguId: string,
  statusIds: number[],
  signal?: AbortSignal,
): Promise<ApplicationManagerStatusDto[]> {
  const envelope = await requestEnvelope<ApplicationManagerStatusesDto>(
    'PUT',
    applicationManagerStatusesPath(pguId),
    ['data'],
    // `method` duplicated in `init` on purpose — see `patchApplication` for why.
    { method: 'PUT', signal, headers: JSON_HEADERS, body: JSON.stringify({ statusIds }) },
  );
  return envelope.data?.items ?? [];
}

/** #28.1 — 201 created; a taken label is a 409 the tab prints under the form. */
export async function createManagerStatus(
  input: ManagerStatusCreateInput,
  signal?: AbortSignal,
): Promise<ManagerStatusDto> {
  const envelope = await requestEnvelope<ManagerStatusDto>('POST', MANAGER_STATUSES_PATH, ['data'], {
    method: 'POST',
    signal,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input satisfies ManagerStatusCreateInput),
  });
  return envelope.data;
}

/** #28.2 — rename, reorder, deactivate/reactivate. There is NO delete to call. */
export async function patchManagerStatus(
  statusId: number,
  patch: ManagerStatusPatchInput,
  signal?: AbortSignal,
): Promise<ManagerStatusDto> {
  const envelope = await requestEnvelope<ManagerStatusDto>('PATCH', managerStatusPath(statusId), ['data'], {
    method: 'PATCH',
    signal,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch satisfies ManagerStatusPatchInput),
  });
  return envelope.data;
}

// ---------------------------------------------------------------------------
// Cache keys
// ---------------------------------------------------------------------------

/** Shared identity of the dictionary — one read for the filter bar AND the profile. */
export const MANAGER_STATUSES_DICTIONARY_KEY = ['manager-statuses', 'dictionary'] as const;

const STATUSES_ROOT = ['admin', 'manager-statuses'] as const;

/**
 * #28 for the Settings tab. `includeInactive` is part of the cache key: the tab's
 * «показать деактивированные» toggle is a second read, not a client-side filter,
 * because the api is the one that knows the row's `active` flag.
 */
export function managerStatusesQueryKey(includeInactive: boolean): readonly [string, string, boolean] {
  return [...STATUSES_ROOT, includeInactive] as const;
}

export function applicationManagerStatusesQueryKey(pguId: string): readonly [string, string, string] {
  return ['manager-statuses', 'application', pguId];
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/** #28, active only. 5 min: the dictionary changes at the pace of the Settings tab. */
export function useManagerStatusDictionary() {
  return useQuery({
    queryKey: MANAGER_STATUSES_DICTIONARY_KEY,
    queryFn: ({ signal }) => fetchManagerStatuses(signal),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: 0,
  });
}

/** #28, raw — the Settings tab unwraps and sorts through `statuses-model.ts`. */
export function useManagerStatuses(includeInactive = false) {
  return useQuery({
    queryKey: managerStatusesQueryKey(includeInactive),
    queryFn: ({ signal }) => fetchManagerStatusesRaw(includeInactive, signal),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    retry: 0,
  });
}

function useInvalidateStatuses(): () => void {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: STATUSES_ROOT });
    // Union seam: the T.Y10 dictionary cache (the register filter + the profile
    // combobox) lives under its own key, and an admin rename/deactivate must
    // reach it too, not only the tab's own rows.
    void queryClient.invalidateQueries({ queryKey: MANAGER_STATUSES_DICTIONARY_KEY });
  };
}

/** #28.1 — the create write; both cache halves re-read so the new label lands everywhere. */
export function useCreateManagerStatus() {
  const invalidate = useInvalidateStatuses();
  return useMutation({ mutationFn: (input: ManagerStatusCreateInput) => createManagerStatus(input), onSettled: invalidate });
}

/**
 * #28.2 — the rename/deactivate write. The api writes the `ENTITY_CHANGE` audit
 * row in the same transaction; the journal itself is read elsewhere and needs no
 * invalidation from here.
 */
export function usePatchManagerStatus() {
  const invalidate = useInvalidateStatuses();
  return useMutation({
    mutationFn: (input: { statusId: number; patch: ManagerStatusPatchInput }) =>
      patchManagerStatus(input.statusId, input.patch),
    onSettled: invalidate,
  });
}

/** #5.5 — the journal of one application, enabled only when there is a pguId. */
export function useApplicationManagerStatuses(pguId: string | null) {
  return useQuery({
    queryKey: applicationManagerStatusesQueryKey(pguId ?? ''),
    queryFn: ({ signal }) => fetchApplicationManagerStatuses(pguId as string, signal),
    enabled: pguId !== null,
    staleTime: 15_000,
    refetchOnWindowFocus: false,
    retry: 0,
  });
}

/**
 * #5.6 — the write. Optimistic over the journal cache: the chosen id lands in the
 * profile the moment the combobox commits (the same rule `usePatchApplication`
 * follows for the manual repairs), and a 400 (unknown / inactive id) rolls the row
 * back with the api's message instead of leaving the optimistic value on screen.
 * On settle the invalidation list is applied — the journal (#5.5), the application
 * detail (#4, which carries the same journal additively as `managerStatuses`) and,
 * since 1.8.2 / T.Z17, the REGISTER root: every #3 row carries the resolved label
 * now, so the «Статус» cell of an already-loaded page follows the drawer in the
 * same moment, and a slice filtered by that status re-applies the server filter —
 * the row leaves/enters the visible set without a manual reload.
 */
export function useSetApplicationManagerStatuses(pguId: string) {
  const queryClient = useQueryClient();
  const journalKey = applicationManagerStatusesQueryKey(pguId);

  return useMutation({
    mutationFn: (statusIds: number[]) => putApplicationManagerStatuses(pguId, statusIds),
    onMutate: async (statusIds) => {
      await queryClient.cancelQueries({ queryKey: journalKey });
      const previous = queryClient.getQueryData<ApplicationManagerStatusDto[]>(journalKey);
      // A brand-new id is not in the journal yet — its label comes from the
      // dictionary cache (#28), so the optimistic badge never renders empty.
      const labels = new Map(
        (queryClient.getQueryData<ManagerStatusDto[]>(MANAGER_STATUSES_DICTIONARY_KEY) ?? []).map((entry) => [
          entry.id,
          entry.label,
        ]),
      );
      for (const row of previous ?? []) if (!labels.has(row.statusId)) labels.set(row.statusId, row.label);
      queryClient.setQueryData<ApplicationManagerStatusDto[]>(journalKey, (current) =>
        optimisticJournal(current, statusIds, labels),
      );
      return { previous };
    },
    onError: (_error, _statusIds, context) => {
      if (context?.previous) queryClient.setQueryData(journalKey, context.previous);
    },
    onSettled: () => {
      // The list is the T.Z17 bridge — the key list is the pure half, so the unit
      // suite pins that a refactor cannot drop the register root silently.
      for (const key of managerStatusWriteInvalidationKeys(pguId)) {
        void queryClient.invalidateQueries({ queryKey: key });
      }
    },
  });
}
